import { useCallback, useState } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useTranslate } from 'lib/tolgee'
import { analyticsTracker, datadog, TOLGEE_GLOBALS } from 'utils'
import { disableProductFruits, enableProductFruits } from 'utils/disable-productfruits-on-export'

import { CHART_EXPORT_ERROR_LOG } from './use-chart-export.consts'
import type {
  TChartRef,
  IChartWithExport,
  TExportType,
  IUseChartExportOptions,
  IUseChartExportReturn,
} from './use-chart-export.types'

export const useChartExport = (chartRef: TChartRef, options?: IUseChartExportOptions): IUseChartExportReturn => {
  const [isExporting, setIsExporting] = useState(false)
  const { createToast } = useToast()
  const t = useTranslate()

  const exportChart = useCallback(
    async (exportType: TExportType = 'image/png', chartTitle?: string) => {
      if (!chartRef.current) {
        console.warn('[useChartExport] Chart ref is not available')
        return
      }

      const removedElements = disableProductFruits()

      try {
        setIsExporting(true)
        createToast({
          content: t(TOLGEE_GLOBALS.startDownload),
        })

        const exportConfig = {
          type: exportType,
        }

        if ('exportChart' in chartRef.current) {
          await chartRef.current.exportChart(exportConfig)
        } else {
          await (chartRef.current.chart as IChartWithExport).exportChart(exportConfig)
        }

        createToast({
          content: t(TOLGEE_GLOBALS.downloadSuccess),
        })

        const { PRINT_ELEMENT } = analyticsTracker.eventNames
        if (!options?.skipDefaultTracking) {
          analyticsTracker.eventTrack(PRINT_ELEMENT, {
            printedElement: chartTitle || 'Chart',
            printSource: 'Export button',
            exportType,
          })
        }

        options?.onSuccess?.()
      } catch (error) {
        datadog.dispatchGenericErrorLog({
          log: CHART_EXPORT_ERROR_LOG,
          error: error as Error,
        })

        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })

        options?.onError?.(error as Error)
      } finally {
        enableProductFruits(removedElements)
        setIsExporting(false)
      }
    },
    [chartRef, options, createToast, t]
  )

  return { exportChart, isExporting }
}
